<section class="teams-shell">
	<header class="global-bar" aria-label="Global navigation bar">
		<div class="global-brand" [attr.aria-label]="brand.name + ' brand'">
			<span class="global-brand__logo" aria-hidden="true">
				<img [src]="brand.logoPath" alt="" />
			</span>
			<span class="global-brand__wordmark">{{ brand.name }}</span>
		</div>

		<!-- Profile actions and the account menu live in the header, outside the chat layout. -->
		<div class="global-bar__actions">
			<div class="task-notifications-area">
				<button
					class="global-icon-btn global-icon-btn--notifications"
					type="button"
					aria-label="Open notifications"
					aria-haspopup="menu"
					[attr.aria-expanded]="isTaskNotificationsOpen()"
					(click)="toggleTaskNotifications($event)"
				>
					<span class="material-icons">notifications</span>
					@if (unreadBellNotificationCount() > 0) {
						<span class="global-icon-btn__badge">{{ unreadBellNotificationCount() > 99 ? '99+' : unreadBellNotificationCount() }}</span>
					}
				</button>

				@if (isTaskNotificationsOpen()) {
					<section class="task-notifications-menu" role="menu" aria-label="Notifications" (click)="onTaskNotificationsMenuClick($event)">
						<div class="task-notifications-menu__header">
							<strong>Notifications</strong>
							<div class="task-notifications-menu__actions">
								<button type="button" class="task-notifications-menu__text-btn" [disabled]="!unreadBellNotificationCount()" (click)="markAllBellNotificationsRead()">Mark all read</button>
								<button type="button" class="task-notifications-menu__text-btn" [disabled]="!bellNotifications().length" (click)="clearBellNotifications()">Clear</button>
							</div>
						</div>

						<div class="task-notifications-menu__list">
							@for (notification of bellNotifications(); track notification.id) {
								<button
									type="button"
									class="task-notifications-menu__item"
									[class.task-notifications-menu__item--unread]="isBellNotificationUnread(notification)"
									(click)="openBellNotification(notification)"
								>
									<strong>{{ getBellNotificationTitle(notification) }}</strong>
									<span>{{ getBellNotificationBody(notification) }}</span>
									<small>
										@if (isMentionBellNotification(notification)) {
											Mention
										} @else {
											Task
										}
										• {{ getBellNotificationTimestamp(notification) }}
									</small>
								</button>
							} @empty {
								<p class="task-notifications-menu__empty">No notifications yet.</p>
							}
						</div>
					</section>
				}
			</div>

			<div class="profile-menu-area">
				<span class="global-profile__status" [class]="getCurrentUserStatusClass('global-profile__status')" aria-hidden="true"></span>
				<button
					class="global-profile avatar-presence avatar-presence--compact"
					type="button"
					aria-label="Open profile menu"
					aria-haspopup="menu"
					[attr.aria-expanded]="isProfileMenuOpen()"
					(click)="toggleProfileMenu($event)"
				>
					<span class="global-profile__media" [class.global-profile__media--image]="profileImageUrl()">
						@if (profileImageUrl()) {
							<img [src]="profileImageUrl()" alt="Profile photo" />
						} @else {
							<span>{{ getProfileInitials() }}</span>
						}
					</span>
					
				</button>

				@if (isProfileMenuOpen()) {
					<section class="profile-menu" role="menu" aria-label="Profile options" (click)="onProfileMenuClick($event)">
						<div class="profile-menu__top-row">
							<strong>{{ userInfo()?.organization_name | titlecase}}</strong>
							<button type="button" class="profile-menu__text-btn" (click)="logout()">Sign out</button>
						</div>

						<div class="profile-menu__identity">
							<div class="profile-menu__avatar avatar-presence" [class.profile-menu__avatar--image]="profileImageUrl()">
								<span class="profile-menu__avatar-media">
									@if (profileImageUrl()) {
										<img [src]="profileImageUrl()" alt="Profile photo" />
									} @else {
										{{ getProfileInitials() }}
									}
								</span>
								<span class="profile-menu__status" [class]="getCurrentUserStatusClass('profile-menu__status')" aria-hidden="true"></span>
							</div>
							<div class="profile-menu__meta">
								<strong>{{ userInfo()?.name | titlecase }}</strong>
								<p>{{ userInfo()?.email }}</p>
								<p class="profile-menu__availability">{{ getCurrentAvailabilityLabel() }}</p>
							</div>
						</div>

						<div class="profile-menu__actions">
							<div class="profile-menu__dropdown" [class.profile-menu__dropdown--open]="isStatusDropdownOpen()">
								<button
									type="button"
									class="profile-menu__action profile-menu__action--dropdown"
									role="menuitem"
									[attr.aria-expanded]="isStatusDropdownOpen()"
									(click)="toggleStatusDropdown($event)"
								>
									<span class="profile-menu__status-dot" [class]="getAvailabilityDotClass('profile-menu__status-dot', getCurrentAvailabilityStatus())" aria-hidden="true"></span>
									<span class="profile-menu__action-copy">
										<span class="profile-menu__action-title">Status</span>
										<span class="profile-menu__action-subtitle">{{ getCurrentAvailabilityLabel() }}</span>
									</span>
									<span class="material-icons profile-menu__chevron" aria-hidden="true">
										{{ isStatusDropdownOpen() ? 'expand_less' : 'expand_more' }}
									</span>
								</button>

								@if (isStatusDropdownOpen()) {
									<div class="profile-menu__dropdown-panel" role="menu" aria-label="Availability status options">
										@for (option of availabilityOptions; track option.value) {
											<button
												type="button"
												class="profile-menu__option"
												[class.profile-menu__option--active]="isCurrentAvailabilityStatus(option.value)"
												role="menuitemradio"
												[attr.aria-checked]="isCurrentAvailabilityStatus(option.value)"
												(click)="onAvailabilityStatusSelected(option.value)"
											>
												<span class="profile-menu__status-dot" [class]="getAvailabilityDotClass('profile-menu__status-dot', option.value)" aria-hidden="true"></span>
												<span>{{ option.label }}</span>
												@if (isCurrentAvailabilityStatus(option.value)) {
													<span class="material-icons profile-menu__option-check" aria-hidden="true">check</span>
												}
											</button>
										}
									</div>
								}
							</div>
						</div>
					</section>
				}
			</div>
		</div>
	</header>

	<div class="chat-layout">
		<!-- Left rail changes the high-level shell mode (chat, calendar, calls, settings). -->
		<app-chat-app-rail
			[items]="appMenu"
			[notificationCounts]="menuNotificationCounts()"
			[selectedKey]="selectedMenu()"
			[appVersion]="appVersion"
			(menuSelect)="onMenuSelect($event)"
		></app-chat-app-rail>

		@if (selectedMenu() === 'chat') {
			<!-- Sidebar emits chat selection and compose intents back to Home. -->
			<app-chat-sidebar
				[chats]="chats()"
				[selectedChatName]="selectedChatName()"
				[userProfilesById]="chatUserProfilesById()"
				(startNewChat)="onStartNewChat()"
				(selectChat)="onChatSelected($event)"
			></app-chat-sidebar>

			@if (isComposingNewChat()) {
				<!-- New-conversation panel is shown in place of the active chat view. -->
				<app-chat-new-conversation
					[prefillRecipientUserId]="newChatPrefillRecipientUserId()"
					(closeComposer)="onCloseNewChat()"
					(conversationStarted)="onNewConversationStarted($event)"
				></app-chat-new-conversation>
			} @else {
				@if (chats().length === 0) {
					<!-- Empty chat state for users who do not yet have any channels. -->
					<section class="chat-empty-state" aria-label="No chats yet">
						<div class="chat-empty-state__icon" aria-hidden="true">
							<span class="material-icons">chat_bubble_outline</span>
						</div>
						<h2>No conversations yet</h2>
						<p>Start a new chat to begin messaging your team.</p>
						<button type="button" class="chat-empty-state__cta" (click)="onStartNewChat()">
							Start a new chat
						</button>
					</section>
				} @else {
					<!-- Active conversation pane receives messages plus unread-anchor metadata from Home. -->
					<app-chat-conversation
						[activeChannelId]="activeChannelId()"
						[canAddParticipants]="activeChatCanAddParticipants()"
						[canLeaveChannel]="activeChatIsGroup()"
						[contactAvatarUrl]="activeChatAvatarUrl()"
						[contactEmail]="activeChatEmail()"
						[contactName]="selectedChatName()"
						[contactUserId]="activeChatParticipantUserId()"
						[contactPresenceStatus]="activeChatPresenceStatus()"
						[activeGroupCallStatus]="activeGroupMeetingStatus()"
						[currentUserId]="userInfo()?.id ?? null"
						[hasMoreMessages]="hasMoreChatMessages()"
						[isStartingCall]="isStartingMeeting()"
						[isUploadingAttachment]="isUploadingAttachment()"
						[lastReadMessageId]="getActiveChannelLastReadMessageId()"
						[loadingOlderMessages]="isLoadingOlderMessages()"
						[mentionableTasks]="activeChannelMentionableTasks()"
						[mentionableUsers]="activeChannelMentionableUsers()"
						[messages]="messages()"
						[reactionUserNames]="reactionUserNames()"
						[userProfilesById]="chatUserProfilesById()"
						(addParticipantRequest)="onAddParticipantToActiveChannel()"
						(viewParticipantsRequest)="onViewActiveChannelParticipants()"
						(deleteMessageRequest)="onDeleteActiveMessage($event)"
						(editMessageRequest)="onEditActiveMessage($event)"
						(endCallRequest)="onEndActiveCall()"
						(joinCallRequest)="onJoinCall($event)"
						(leaveChannelRequest)="onLeaveActiveChannel()"
						(reactionToggleRequest)="onToggleMessageReaction($event)"
						(renameGroupRequest)="onRenameActiveChannel()"
						(reachedConversationTop)="onConversationReachedTop()"
						(startCallRequest)="onStartActiveCall($event)"
						(taskReferenceOpenRequest)="onTaskReferenceOpen($event)"
						[typingParticipants]="activeTypingParticipants()"
						(reachedConversationBottom)="onConversationReachedBottom()"
						(sendMessage)="onConversationSendMessage($event)"
						(typingStateChange)="onConversationTypingStateChange($event)"
					></app-chat-conversation>
				}
			}
		} @else if (selectedMenu() === 'tasks') {
			<app-tasks-screen class="menu-screen-host" [notificationTarget]="taskNotificationTarget()"></app-tasks-screen>
		} @else if (selectedMenu() === 'todo') {
			<app-todo-screen class="menu-screen-host"></app-todo-screen>
		} @else if (selectedMenu() === 'calendar') {
			<!-- Non-chat shell modes reuse the same outer layout area. -->
			<app-calendar-screen class="menu-screen-host"></app-calendar-screen>
		} @else if (selectedMenu() === 'calls') {
			<app-calls-screen
				class="menu-screen-host"
				[logs]="callLogs()"
				(startCallRequest)="onStartCallFromLogs($event)"
			></app-calls-screen>
		} @else {
			<app-settings-screen
				class="menu-screen-host"
				[userName]="userInfo()?.name || 'User'"
				[userEmail]="userInfo()?.email || ''"
				[userPhotoUrl]="profileImageUrl()"
				[chatSoundsEnabled]="chatSoundsEnabled()"
				[chatSoundVolume]="chatSoundVolume()"
				[canManageUsers]="canManageUsers()"
				[canManageCompany]="canManageCompany()"
				(openProfile)="goToProfilePage()"
				(openChangePassword)="openChangePasswordDialog()"
				(openManagement)="goToManagementPage()"
				(toggleChatSounds)="onToggleChatSounds()"
				(chatSoundVolumeChange)="onChatSoundVolumeChange($event)"
			></app-settings-screen>
		}
	</div>
</section>
